import type { UseQueryResult } from '@tanstack/react-query';
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';
import type { SelectParams } from '@/types/api/select';
import { cn } from '@/lib/utils';
import { AsyncSelect } from '../async-select';
import { type ColSpan, spanToClassName } from './form-input';

type FormAsyncSelectProps<T extends FieldValues, TOption> = {
  name: FieldPath<T>;
  control: Control<T>;
  label: string;

  placeholder?: string;

  sm?: ColSpan;
  md?: ColSpan;
  lg?: ColSpan;

  className?: string;
  required?: boolean;

  useQuery: (params?: SelectParams) => UseQueryResult<TOption[], Error>;
  getOptionValue: (option: TOption) => string;
  getDisplayValue: (option: TOption) => string;

  /** Guarda o objeto opção inteiro no campo em vez de só o id. */
  returnOption?: boolean;
  onSelect?: (option: TOption | null) => void;

  clearable?: boolean;
  disabled?: boolean;

  onCreate?: (search: string) => Promise<TOption | null | undefined>;
  createLabel?: (search: string) => React.ReactNode;
};

export function FormAsyncSelect<T extends FieldValues, TOption>({
  name,
  control,
  label,
  placeholder,

  sm,
  md,
  lg,

  className,
  required,

  useQuery,
  getOptionValue,
  getDisplayValue,

  returnOption,
  onSelect,

  clearable,
  disabled,

  onCreate,
  createLabel,
}: FormAsyncSelectProps<T, TOption>) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <div className={cn(spanToClassName(sm, md, lg), className)}>
          <AsyncSelect<TOption>
            label={label}
            required={required}
            invalid={fieldState.invalid}
            disabled={disabled}
            useQuery={useQuery}
            getOptionValue={getOptionValue}
            getDisplayValue={getDisplayValue}
            placeholder={placeholder}
            value={
              (returnOption
                ? field.value
                  ? getOptionValue(field.value)
                  : ''
                : field.value) ?? ''
            }
            onChange={(value, option) => {
              field.onChange(returnOption ? option : value);
              onSelect?.(option);
            }}
            clearable={clearable}
            onCreate={onCreate}
            createLabel={createLabel}
          />

          {fieldState.error && (
            <p className="mt-1.5 text-xs font-medium text-destructive">
              {fieldState.error.message}
            </p>
          )}
        </div>
      )}
    />
  );
}
